import type { ElementType, HTMLAttributes, ReactElement } from "react";
import styles from "./Text.module.css";

export type TextVariant =
  | "display"
  | "title"
  | "heading"
  | "subheading"
  | "body"
  | "bodyLarge"
  | "caption"
  | "eyebrow"
  | "identifier";

export type TextTone = "primary" | "secondary" | "muted" | "accent" | "onBrand" | "inverse";

export interface TextProps extends HTMLAttributes<HTMLElement> {
  /** Visual style. Decoupled from the element — pick `as` for semantics separately. */
  variant?: TextVariant;
  tone?: TextTone;
  /**
   * The rendered element. Choose it for correct document semantics (e.g. `h1`
   * for the page title, `p` for body) independently of the visual `variant`.
   */
  as?: ElementType;
  /** Balance wrapping across lines — good for headings. */
  balance?: boolean;
}

const DEFAULT_ELEMENT: Record<TextVariant, ElementType> = {
  display: "h1",
  title: "h2",
  heading: "h3",
  subheading: "h4",
  body: "p",
  bodyLarge: "p",
  caption: "span",
  eyebrow: "span",
  identifier: "span",
};

/**
 * Typography primitive. `variant` drives the look; `as` drives the semantics —
 * kept independent so a visually-large label can still be a semantically-correct
 * heading (or vice versa). Every text style in the app flows through here.
 */
export function Text({
  variant = "body",
  tone = "primary",
  as,
  balance = false,
  className,
  children,
  ...rest
}: TextProps): ReactElement {
  const Component = as ?? DEFAULT_ELEMENT[variant];
  const classes = [
    styles.text,
    styles[variant],
    styles[`tone-${tone}`],
    balance ? styles.balance : undefined,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
